import type { OperationsStartRiskRowDto } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { deltaClass, formatDelta, START_RISK_CLASS, START_RISK_LABEL, START_RISK_ORDER, startRiskTip } from "../operationsLabels";

interface Props {
  rows: OperationsStartRiskRowDto[];
  /** Flow open-now on the full population: what the percentages read against. */
  openNow: number;
  atRiskAfterBd: number;
  breachedAfterBd: number;
  active?: "atRisk" | "breached";
  /** Opens the journey drawer on the cohort, optionally one work type (Onboarding / Extension). */
  onDrill: (risk: "atRisk" | "breached", workType?: "Onboarding" | "Extension") => void;
}

/**
 * The start-date row under the attention tiles (Rafael, 2026-10-24): At risk and Breached, each open journey
 * once by its carrier's work-order version start date. A drill, not a lens: the other cards keep counting all
 * open work. Growth is bad on both tiles.
 */
export function StartDateTiles({ rows, openNow, atRiskAfterBd, breachedAfterBd, active, onDrill }: Props) {
  const byRisk = new Map(rows.map((r) => [r.risk, r]));
  return (
    <section className="ops-span-12" aria-label="Start date">
      <div className="ops-tiles two">
        {START_RISK_ORDER.map((risk) => {
          const row = byRisk.get(risk);
          const count = row?.count ?? 0;
          const delta = row?.delta ?? 0;
          const pct = openNow ? ((count / openNow) * 100).toFixed(1) : "0.0";
          const tone = delta > 0 ? "bad" : delta < 0 ? "good" : "flat";
          const worst = row?.worstLeadBd ?? null;
          const split = (workType: "Onboarding" | "Extension", n: number) => (
            <span
              className="ops-tile-split"
              role="link"
              tabIndex={0}
              title={`List the ${workType === "Onboarding" ? "onboarding journeys" : "extensions"} in this tile.`}
              onClick={(e) => { e.stopPropagation(); onDrill(risk, workType); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDrill(risk, workType); } }}
            >
              {n} {workType === "Onboarding" ? (n === 1 ? "onboarding" : "onboardings") : (n === 1 ? "extension" : "extensions")}
            </span>
          );
          return (
            <button
              key={risk}
              type="button"
              className={`ops-tile risk-${START_RISK_CLASS[risk]}${active === risk ? " active" : ""}`}
              onClick={() => onDrill(risk)}
              aria-pressed={active === risk}
              aria-label={`${START_RISK_LABEL[risk]}: ${count} open`}
            >
              <span className="lbl">
                {START_RISK_LABEL[risk]}
                <InfoTooltip inline text={startRiskTip(risk, atRiskAfterBd, breachedAfterBd)} />
              </span>
              <span className="val">{count}</span>
              <span className="sub">
                <span>{risk === "atRisk" ? `unapproved, starts within ${atRiskAfterBd} bd` : breachedAfterBd > 0 ? `unapproved, started ${breachedAfterBd}+ bd ago` : "unapproved on or after the start date"}</span>
                <span aria-hidden="true">·</span>
                <span>{pct}% of open</span>
                <span aria-hidden="true">·</span>
                <span className={`ops-delta ${deltaClass(delta)} ${tone}`}>{formatDelta(delta)} DoD</span>
                {row && (row.onboardings > 0 || row.extensions > 0) && (
                  <>
                    <span aria-hidden="true">·</span>
                    {split("Onboarding", row.onboardings)}
                    <span aria-hidden="true">·</span>
                    {split("Extension", row.extensions)}
                  </>
                )}
                {worst != null && count > 0 && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{risk === "atRisk" ? `soonest in ${worst} bd` : worst === 0 ? "latest starts today" : `oldest ${-worst} bd late`}</span>
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
